/* ========================================================================= *
 * bowlEligibility.js — the page's logic, and the sentences it must say.
 *
 * Extracted from the component for two reasons:
 *
 *   1. `verify-bowl-eligibility.mjs` imports THIS, not the JSX, so the rules
 *      can be asserted without a browser.
 *   2. `check-shipped-features.mjs` greps the BUILT bundle, and the JSX
 *      transform splits multi-line element text into separate literals. A long
 *      sentence written inline in JSX therefore cannot be asserted at all. The
 *      honesty clauses below are named constants precisely so the publish gate
 *      can prove they are still in the bundle.
 *
 * ⚠️ WHAT THE GATE ASSERTS IS THE CLAUSES, NEVER THE COUNTS. The counts move
 * every Saturday. A gate pinned to "5 eligible" would go red on an ordinary
 * football result, and a gate that fails for correct reasons teaches everybody
 * to edit the gate.
 * ========================================================================= */

/* ---- the sentences the surface must keep saying ------------------------ */

/** Why three groups and not one. */
export const THREE_GROUPS_NOTE =
  'Eligible, evidence incomplete, still alive and eliminated are separate groups. An eligible-only board hides the remaining postseason work in October and December alike.'

/** The one thing this board will not assert. */
export const CONTINGENT_NOTE =
  'Contingent is not a maybe about the team: countable-win evidence is incomplete. A missing opponent ledger is not proof of an FCS win. '
  + 'The applied one-FCS-win ruling is identified on eligible rows; it is a named decision, not verification of the scholarship test the NCAA does not publish.'

/** Why the elimination column is empty in October. */
export const ELIMINATED_EMPTY_NOTE =
  'A successfully reconciled board’s empty eliminated group reflects the arithmetic, not a missing read. No eliminated teams were returned in this board. That is this snapshot’s classification, not a claim that elimination is impossible at this time of year.'

/** Membership, and why it is not the number people remember. */
export const MEMBERSHIP_NOTE =
  'Membership is 138 this season, not 136. North Dakota State and Sacramento State moved up from '
  + 'FCS, and a June 2026 rule makes a transitional team bowl eligible at 6-6 with at least five '
  + 'wins over FBS opponents.'

/** The staleness warning. This is the whole reason the stamp is on the page. */
export const AS_OF_NOTE =
  'Records are as of the source\'s own stamp, not the moment you opened this page. A stale '
  + 'standings page looks identical to a current one.'

/** What the reconciliation is for. */
export const RECONCILE_NOTE =
  'Every one of the 138 teams appears in exactly one group, and the conference blocks sum to the '
  + 'same total. The undefeated count is an independent control: the NCAA publishes its own, and '
  + 'a mismatch means the table was read short.'

/** The empty state, which is unconditional and therefore the only string that
 *  proves the block is mounted at all. */
export const NO_SNAPSHOT_NOTE =
  'No weekly board has been stored yet. This is the absence of a board, not an empty one.'

/** The failure state. Never a blank that reads as "nobody is eligible". */
export const UNREADABLE_NOTE =
  'Could not load the bowl eligibility board.'

/** Why the trigger list, not the board, is the thing outbound acts on. */
export const TRIGGER_NOTE =
  'Became eligible, not is eligible. A board can answer "who is in" forever and never answer '
  + '"who just got in", and the second is what starts outbound and the only one a KPI can '
  + 'measure. Each row is stamped with the week it crossed, once — the trigger latches, so it '
  + 'cannot fire twice and cannot be double-counted.';

/** The empty trigger list, which is the normal answer most weeks. */
export const NO_TRIGGERS_NOTE =
  'For this read, the answer is none: no eligibility transitions were returned. This does not establish that no team became eligible outside the available history or scope.'

/** Coverage is not ownership, and the difference is said out loud. */
export const COVERAGE_NOTE =
  'This is the rep who covers the conference, from the 2026-2027 assignment breakdown. It is not '
  + 'the school account\'s own assigned rep, which is authoritative and may differ — matching the '
  + 'school to its account record is a separate confirmation and is not done yet.';

/** An unowned newly eligible school is the thing that must not render blank. */
export const UNOWNED_NOTE =
  'No desk can be derived for these. That is a gap in the assignment breakdown, not a blank cell '
  + '— a newly eligible school nobody is accountable for is how a trigger goes nowhere.';

/** What this page is NOT, so nobody reads it as championship data. */
export const NOT_A_CHAMPIONSHIP_NOTE =
  'Bowl eligibility is not a championship. The NCAA does not crown FBS football — the College '
  + 'Football Playoff does — so none of this is written to the championship universe. It is sales '
  + 'intelligence: who is heading for a postseason game, and how early we can know.'

/* ---- groups ------------------------------------------------------------ */

/**
 * The display order, and it is deliberate: the two groups a rep acts on come
 * first, and the one that fills up in November comes last.
 */
export const GROUPS = [
  { key: 'eligible',   label: 'In',          blurb: 'Enough countable wins under the applied rule. Each row distinguishes unconditional evidence from the named one-FCS-win ruling.' },
  { key: 'contingent', label: 'Contingent',  blurb: 'Six or more wins, but the evidence needed to establish countable wins is incomplete.' },
  { key: 'alive',      label: 'Still alive', blurb: 'Can still reach six wins.' },
  { key: 'eliminated', label: 'Out',         blurb: 'Cannot reach six wins from here.' },
]

export const GROUP_KEYS = GROUPS.map((g) => g.key)

/** Group a team list, preserving the order teams arrived in. */
export function byGroup(teams = []) {
  const out = {}
  for (const k of GROUP_KEYS) out[k] = []
  for (const t of teams) if (out[t?.group]) out[t.group].push(t)
  return out
}

/**
 * Does a snapshot reconcile against its own membership?
 *
 * Checked on the CLIENT as well as the server, and that redundancy is the
 * point: the server refuses to STORE a board that does not reconcile, and this
 * refuses to RENDER one. A number on a dashboard that does not add up is worse
 * than no dashboard, and the two checks fail for different reasons — a bad
 * write versus a bad read.
 */
export function reconciles(snapshot) {
  if (!snapshot) return false
  const c = snapshot.counts || {}
  const sum = GROUP_KEYS.reduce((n, k) => n + (c[k] || 0), 0)
  return Boolean(snapshot.membership) && sum === snapshot.membership
}

/** Teams sorted the way a rep reads them: closest to the line first. */
export function sortForGroup(key, teams = []) {
  const list = [...teams]
  if (key === 'alive') return list.sort((a, b) => (a.needs - b.needs) || (b.wins - a.wins) || a.name.localeCompare(b.name))
  if (key === 'eliminated') return list.sort((a, b) => (b.losses - a.losses) || a.name.localeCompare(b.name))
  return list.sort((a, b) => (b.wins - a.wins) || a.name.localeCompare(b.name))
}

/**
 * How old is this board, in plain words?
 *
 * Returns `{ text, level }` where level is 'ok' | 'warn' | 'bad'. The board is
 * written once a week, so one week is normal and two is a dead engine.
 */
export function freshness(composedAt, now = Date.now()) {
  if (!composedAt) return { text: 'no date on this board', level: 'bad' }
  const ms = now - new Date(composedAt).getTime()
  if (Number.isNaN(ms)) return { text: 'unreadable date', level: 'bad' }
  const hours = Math.round(ms / 3_600_000)
  const days = Math.floor(hours / 24)
  const text = hours < 1 ? 'read minutes ago'
    : hours < 24 ? `read ${hours} hour${hours === 1 ? '' : 's'} ago`
    : `read ${days} day${days === 1 ? '' : 's'} ago`
  const level = days >= 14 ? 'bad' : days >= 8 ? 'warn' : 'ok'
  return { text, level, hours, days }
}

/* ---- times, in Eastern, with the zone the date actually had (2026-11-01) ----
 * The board printed the NCAA's "09:16 AM EDT" the morning daylight saving
 * ended, and a browser-local toLocaleDateString() for "Crossed", so Duke and
 * Iowa (first seen by a Saturday-night run, 00:23Z) read 10/31 while teams
 * first seen Sunday morning read 11/1, for the same Saturday slate. */
const ET = 'America/New_York'
const MONTHS = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 }

/** "8:09 AM EST" for an instant, in Eastern with the correct EST/EDT. */
export function etTime(iso, { withDay = false } = {}) {
  const d = new Date(iso)
  if (!iso || Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    ...(withDay ? { weekday: 'short', month: 'short', day: 'numeric' } : {}) }).format(d)
}

/** "Sun Nov 1" for a YYYY-MM-DD week key (a calendar date, no zone). */
export function weekLabel(weekKey) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(weekKey || ''))
  if (!m) return ''
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12))
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }).format(d)
}

/** The Crossed cell: ONE label per latch week, so a slate reads the same for every team. */
export function crossedCell(trigger = {}) {
  const week = weekLabel(trigger.weekKey)
  return { text: week ? `week ending ${week}` : '', firstSeen: trigger.becameAt ? `first seen ${etTime(trigger.becameAt, { withDay: true })}` : '' }
}

/**
 * The NCAA's own "Last Updated" text, checked against when we read it.
 * Returns { text, warn } where warn names the problem, or ''. Measured on the
 * live weeks: "Oct 25, 2026 09:04 AM EDT" was read at 12:10Z and "Nov 01, 2026
 * 09:16 AM EDT" at 13:09Z, both EARLIER than the stamp claims in Eastern time,
 * so the page's zone label cannot be Eastern. It is shown as the NCAA's words.
 */
export function sourceStampCheck(sourceStamp, readAtIso) {
  const s = String(sourceStamp || '').trim()
  const m = /^([A-Z][a-z]{2})\s+(\d{1,2}),\s+(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)?\s*(EDT|EST)?/.exec(s)
  if (!m || MONTHS[m[1]] == null) return { text: s, warn: '' }
  let h = +m[4]
  if (h <= 12 && m[6] === 'PM' && h !== 12) h += 12
  if (h === 12 && m[6] === 'AM') h = 0
  const label = m[7] || ''
  const offset = label === 'EDT' ? 4 : label === 'EST' ? 5 : null
  const noonUtc = new Date(Date.UTC(+m[3], MONTHS[m[1]], +m[2], 17))
  const actualZone = new Intl.DateTimeFormat('en-US', { timeZone: ET, timeZoneName: 'short' }).format(noonUtc).split(' ').pop()
  const problems = []
  if (label && actualZone && label !== actualZone) problems.push(`the NCAA labels it ${label}, but Eastern time on that date is ${actualZone}`)
  if (offset != null && readAtIso) {
    const claimed = Date.UTC(+m[3], MONTHS[m[1]], +m[2], h + offset, +m[5])
    if (claimed > new Date(readAtIso).getTime() + 60_000) problems.push(`it is later than when we read the page (${etTime(readAtIso)}), so its zone label is not Eastern`)
  }
  return { text: s, warn: problems.length ? `NCAA's own wording; ${problems.join('; ')}.` : '' }
}

/** Every landing this week, as lines: the one that composed the board first. */
export function landingLines(snap = {}) {
  const list = Array.isArray(snap.landings) ? snap.landings : []
  const who = (l) => (l.agent === 'human' ? `a person (${l.actor || 'unknown'})`
    : l.agent === 'jarvis' ? 'Jarvis' : l.agent === 'dot' ? 'Dot'
      : `${String(l.actor || '').startsWith('svc:') ? `engine ${l.actor.slice(4)}` : l.actor || 'an unknown caller'}, unstamped`)
  return list.map((l) => `${l.changed === false ? 'Re-read' : 'Landed'} by ${who(l)} at ${etTime(l.at)}${l.changed === false ? ', no change' : ''}`)
}

/** Contingent, said plainly (2026-11-01). Replaces "six wins the NCAA will not let anyone evidence". */
export const contingentLine = (n) => `${n} more have six wins and are waiting on proof of which wins were over FCS teams`

/** The number formatter the rest of the product uses. An em dash, never a 0. */
export const nf = (n) => (typeof n === 'number' ? n.toLocaleString() : '—')

export const record = (t) => `${t.wins}–${t.losses}`
